// src/components/StockManagement/SearchableDropdown.jsx
import React, { useState, useRef, useEffect } from 'react';

const SearchableDropdown = ({
  products = [],
  value,
  onChange,
  name,
  disabled = false,
  placeholder = 'Select Product',
  emptyMessage = 'No products available'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update search term when value is selected
  useEffect(() => {
    if (value) {
      const selectedProduct = products.find(p => p && p.id === parseInt(value));
      if (selectedProduct?.name) {
        setSearchTerm(selectedProduct.name);
      }
    } else {
      setSearchTerm('');
    }
  }, [value, products]);

  // Filter products based on search term with safety checks
  const filteredProducts = products.filter(product => {
    if (!product || !product.name) return false;
    
    if (!searchTerm) return true;
    
    const productName = String(product.name).toLowerCase();
    const search = String(searchTerm).toLowerCase();
    
    return productName.includes(search);
  });

  const handleSelect = (product) => {
    if (!product || !product.id) return;
    
    onChange({
      target: {
        name,
        value: String(product.id)
      }
    });
    setSearchTerm(product.name);
    setIsOpen(false);
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    
    // Clear selection if user is typing
    if (value && newValue !== products.find(p => p?.id === parseInt(value))?.name) {
      onChange({
        target: {
          name,
          value: ''
        }
      });
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const styles = {
    container: {
      position: 'relative',
      width: '100%'
    },
    menu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: '4px',
      background: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      zIndex: 1000,
      maxHeight: '300px',
      display: 'flex',
      flexDirection: 'column'
    },
    list: {
      overflowY: 'auto',
      maxHeight: '300px'
    },
    item: {
      padding: '10px 12px',
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      borderBottom: '1px solid #f5f5f5'
    },
    itemHover: {
      backgroundColor: '#f0f0f0'
    },
    itemSelected: {
      backgroundColor: '#e3f2fd',
      fontWeight: 500
    },
    empty: {
      padding: '20px',
      textAlign: 'center',
      color: '#999',
      fontStyle: 'italic'
    }
  };

  return (
    <div style={styles.container} ref={dropdownRef}>
      <input
        type="text"
        className="stock-out-input"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleSearchChange}
        onFocus={handleFocus}
        disabled={disabled}
        autoComplete="off"
      />

      {isOpen && !disabled && (
        <div style={styles.menu}>
          <div style={styles.list}>
            {filteredProducts.length === 0 ? (
              <div style={styles.empty}>
                {searchTerm ? 'No matching products' : emptyMessage}
              </div>
            ) : (
              filteredProducts.map(product => (
                <div
                  key={product.id}
                  style={{
                    ...styles.item,
                    ...(product.id === parseInt(value) ? styles.itemSelected : {})
                  }}
                  onMouseEnter={(e) => {
                    if (product.id !== parseInt(value)) {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (product.id !== parseInt(value)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  onClick={() => handleSelect(product)}
                >
                  {product.name}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;